import { Box, Button, Flex, Skeleton, Text } from '@chakra-ui/react';
import {
  ModalOrDrawerBodyWithMask,
  ModalOrDrawerFooter,
} from '@ui/components/ModalOrDrawer';
import { useTranslations } from '@ui/i18n';
import { useVenoSDK } from '@ui/providers/VenoSDKProvider';
import {
  getNftCollectionEndTime,
  getNftCollections,
  useIsNftCollectionDisabled,
} from '@ui/queries/types';
import type { NftCollection } from '@veno-app/sdk';
import React, { useMemo } from 'react';

import NftSelection from '../GardenNftDrawersShared/NftSelection';
import SelectedNftPreview from '../GardenNftDrawersShared/SelectedNftPreview';
import {
  STEP,
  useGardenNftDrawerStore,
} from '../GardenNftDrawersShared/useGardenNftDrawer';
import ExpandableCollection from './ExpandableCollection';
import useStakeNftInfo from './useStakeNftInfo';

function DefaultContent() {
  const t = useTranslations();
  const sdk = useVenoSDK();

  const setStep = useGardenNftDrawerStore((s) => s.setStep);
  const selectedTokens = useGardenNftDrawerStore((s) => s.selectedTokens);
  const setSelectedTokens = useGardenNftDrawerStore((s) => s.setSelectedTokens);

  const { nfts, maxNForStake } = useStakeNftInfo();
  const collectionDisabledMap = useCollectionDisabledMap();
  const nftCollections = useMemo(
    () => getNftCollections(sdk.chainType),
    [sdk.chainType],
  );

  const collections = useMemo(() => {
    const now = new Date().getTime();
    return nftCollections.map((c) => {
      if (
        collectionDisabledMap[c] === false &&
        (getNftCollectionEndTime(c)?.getTime() ?? Infinity) > now
      ) {
        return nfts?.filter((nft) => nft.collection === c) ?? [];
      }
      return null;
    });
  }, [collectionDisabledMap, nfts, nftCollections]);

  const deselectToken = (tokenId: string) =>
    setSelectedTokens(selectedTokens.filter(({ id }) => id !== tokenId));

  const isLoading = Object.values(collectionDisabledMap).some(
    (v) => typeof v === 'undefined',
  );

  return (
    <>
      <ModalOrDrawerBodyWithMask pt={0}>
        <Box w="full" mb="79px">
          <SelectedNftPreview
            position="fixed"
            zIndex={1}
            bg="bg.main"
            mx="auto"
            left={0}
            right={0}
            w="full"
            onClickNft={(nft) => deselectToken(nft.id)}
            nSelectable={maxNForStake ?? 0}
            nfts={nfts ?? []}
            renderAdditionalContent={(cruiser) => (
              <Text textStyle="captionBold" mt={0.5}>
                {
                  cruiser
                    ? `+${cruiser.multiplier?.toFixed(3)}x`
                    : '\u00A0' /* nbsp so there's no height shift */
                }
              </Text>
            )}
          />
        </Box>
        <Text mx="auto" maxW="300px" textStyle="h3" textAlign="center">
          {maxNForStake !== undefined
            ? t('Select up to {num} NFTs you want to stake', {
                num: maxNForStake,
              })
            : t('Select {num} NFTs you want to stake', {
                num: '--',
              })}
        </Text>
        <Flex mt="24px" flexDir="column" gap={4} mb={2}>
          {collections.map((collection, i) => {
            if (!collection) {
              return null;
            }
            return (
              <ExpandableCollection
                key={i}
                collection={nftCollections[i]}
                collectionNumber={collection?.length}
              >
                <NftSelection
                  collectionToCheckForActive={nftCollections[i]}
                  nfts={collection ?? []}
                  selectedTokens={selectedTokens}
                  onSelect={(id) =>
                    setSelectedTokens(
                      [
                        ...selectedTokens,
                        { collection: nftCollections[i], id },
                      ].slice(0, 5),
                    )
                  }
                  onDeselect={deselectToken}
                  maxNForSelect={maxNForStake}
                />
              </ExpandableCollection>
            );
          })}
        </Flex>
        {isLoading && <Skeleton w="full" h="60px" />}
      </ModalOrDrawerBodyWithMask>
      <ModalOrDrawerFooter>
        <Button
          onClick={() => setStep(STEP.confirming)}
          disabled={!selectedTokens.length}
        >
          {t('Hire Helpers')}
        </Button>
      </ModalOrDrawerFooter>
    </>
  );
}

const useCollectionDisabledMap = () => {
  const collectionDisabledMap: Record<NftCollection, boolean | undefined> =
    useMemo(() => {
      return {
        CRUISER: undefined,
        MOLE: undefined,
        BoomerSquad: undefined,
        Ballies: undefined,
      };
    }, []);
  collectionDisabledMap.CRUISER = useIsNftCollectionDisabled('CRUISER').data;
  collectionDisabledMap.MOLE = useIsNftCollectionDisabled('MOLE').data;
  collectionDisabledMap.BoomerSquad =
    useIsNftCollectionDisabled('BoomerSquad').data;
  collectionDisabledMap.Ballies = useIsNftCollectionDisabled('Ballies').data;

  return collectionDisabledMap;
};

export default DefaultContent;
